import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import jsQR from "jsqr"

const AUTO_SCAN_KEY = "ticketfy.checkin.autoScan"

export default class extends Controller {
  static targets = ["video", "canvas", "permissionDeniedMessage", "idleResult"]
  static values = {
    scanUrl: String,
    previewUrl: String,
    pauseAfterScan: { type: Number, default: 3000 }
  }

  connect() {
    this.paused = false
    this.rafId = null
    this.resumeTimer = null
    this.stream = null
    this.pendingToken = null
    this.startCamera()
  }

  disconnect() {
    this.stopLoop()
    this.stopCamera()
    if (this.resumeTimer) clearTimeout(this.resumeTimer)
  }

  async startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.showPermissionDenied()
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } }
      })
      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
      this.tick()
    } catch {
      this.showPermissionDenied()
    }
  }

  tick() {
    if (this.paused) return

    const video = this.videoTarget
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      const canvas = this.canvasTarget
      const width = video.videoWidth
      const height = video.videoHeight
      if (width && height) {
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, width, height)
        const imageData = ctx.getImageData(0, 0, width, height)
        const code = jsQR(imageData.data, width, height)
        if (code?.data) {
          this.handleScan(code.data)
          return
        }
      }
    }

    this.rafId = requestAnimationFrame(() => this.tick())
  }

  handleScan(raw) {
    const token = this.extractToken(raw)
    if (!token) {
      this.rafId = requestAnimationFrame(() => this.tick())
      return
    }

    this.paused = true
    this.stopLoop()
    this.pendingToken = token

    const auto = this.autoScanEnabled
    const url = auto ? this.scanUrlValue : this.previewUrlValue
    this.submitToken(url, token, { scheduleResume: auto })
  }

  extractToken(raw) {
    const text = raw.toString().trim()
    if (!text) return ""

    let token = text
    try {
      const url = new URL(text)
      const parts = url.pathname.split("/").filter(Boolean)
      token = parts[parts.length - 1] || ""
    } catch {
      const parts = text.split("/").filter(Boolean)
      token = parts[parts.length - 1] || text
    }

    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token)
      ? token
      : ""
  }

  async submitToken(url, token, { scheduleResume } = {}) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        body: new URLSearchParams({ token })
      })
      const html = await response.text()
      if (html) Turbo.renderStreamMessage(html)
      if (!response.ok) {
        this.notifyError()
        this.scheduleResume()
        return
      }
      requestAnimationFrame(() => {
        if (!this.holding()) this.scheduleResume()
      })
    } catch {
      this.notifyError()
      this.scheduleResume()
    }
  }

  confirmPending() {
    if (!this.pendingToken || !this.scanUrlValue || this._confirming) return

    this._confirming = true
    this.submitToken(this.scanUrlValue, this.pendingToken, { scheduleResume: true })
      .finally(() => { this._confirming = false })
  }

  resume() {
    if (this.resumeTimer) clearTimeout(this.resumeTimer)
    this.resumeTimer = null
    this.resetResult()
    this.pendingToken = null
    this.paused = false
    this.tick()
  }

  resetResult() {
    const panel = document.getElementById("checkin-result")
    if (!panel || !this.hasIdleResultTarget) return

    panel.innerHTML = this.idleResultTarget.innerHTML
  }

  scheduleResume() {
    if (this.resumeTimer) clearTimeout(this.resumeTimer)
    this.resumeTimer = setTimeout(() => this.resume(), this.pauseAfterScanValue)
  }

  holding() {
    return !!document.querySelector("#checkin-result [data-checkin-hold]")
  }

  get autoScanEnabled() {
    try {
      const value = localStorage.getItem(AUTO_SCAN_KEY)
      if (value === null) return true
      return value === "1" || value === "true"
    } catch {
      return true
    }
  }

  notifyError() {
    document.dispatchEvent(new CustomEvent("checkin:outcome", { detail: { result: "not_found" } }))
  }

  showPermissionDenied() {
    if (!this.hasPermissionDeniedMessageTarget) return

    this.permissionDeniedMessageTarget.hidden = false
  }

  stopLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = null
  }

  stopCamera() {
    this.stream?.getTracks()?.forEach((track) => track.stop())
    this.stream = null
    if (this.hasVideoTarget) this.videoTarget.srcObject = null
  }
}
